import type { CSSProperties, KeyboardEvent, ReactNode } from "react";

import { cn, hasRenderableNode } from "../base/utils";
import type { WorkbenchVisualizationTone } from "./WorkbenchScatterPlot";

export interface WorkbenchRelationshipNode {
  id: string;
  label: string;
  meta?: string;
  tone?: WorkbenchVisualizationTone;
}

export interface WorkbenchRelationshipEdge {
  id?: string;
  sourceId: string;
  targetId: string;
  label?: string;
}

export interface WorkbenchRelationshipGraphProps {
  nodes: WorkbenchRelationshipNode[];
  edges: WorkbenchRelationshipEdge[];
  selectedNodeId?: string;
  onNodeSelect?: (node: WorkbenchRelationshipNode) => void;
  width?: number;
  height?: number;
  emptyState?: ReactNode;
  ariaLabel?: string;
  className?: string;
  variant?: "default" | "radar";
}

const toneClassNames: Record<WorkbenchVisualizationTone, string> = {
  primary: "fill-primary/10 stroke-primary text-primary",
  secondary:
    "fill-secondary stroke-secondary-foreground text-secondary-foreground",
  accent: "fill-accent stroke-accent-foreground text-accent-foreground",
  muted: "fill-muted stroke-muted-foreground text-muted-foreground",
};

const radarToneVariable: Record<WorkbenchVisualizationTone, string> = {
  primary: "--radar-series-one",
  secondary: "--radar-series-two",
  accent: "--radar-series-three",
  muted: "--radar-series-four",
};

const radarToneFallback: Record<WorkbenchVisualizationTone, string> = {
  primary: "var(--primary)",
  secondary: "var(--secondary-foreground)",
  accent: "var(--accent-foreground)",
  muted: "var(--muted-foreground)",
};

const radarToneColor = (tone: WorkbenchVisualizationTone, alpha = 1) =>
  `hsl(var(${radarToneVariable[tone]}, ${radarToneFallback[tone]}) / ${alpha})`;

const activateNode = (
  event: KeyboardEvent<SVGGElement>,
  node: WorkbenchRelationshipNode,
  onNodeSelect?: (node: WorkbenchRelationshipNode) => void,
) => {
  if (!onNodeSelect || (event.key !== "Enter" && event.key !== " ")) return;
  event.preventDefault();
  onNodeSelect(node);
};

export function WorkbenchRelationshipGraph({
  nodes,
  edges,
  selectedNodeId,
  onNodeSelect,
  width = 900,
  height = 440,
  emptyState = null,
  ariaLabel = "Relationship graph",
  className,
  variant = "default",
}: WorkbenchRelationshipGraphProps) {
  if (!nodes.length) {
    return hasRenderableNode(emptyState) ? <>{emptyState}</> : null;
  }

  const radar = variant === "radar";
  const radius = Math.min(width, height) * (radar ? 0.37 : 0.35);
  const selectedIndex = selectedNodeId
    ? nodes.findIndex((node) => node.id === selectedNodeId)
    : -1;
  const orbitNodes = selectedIndex >= 0
    ? nodes.filter((_, index) => index !== selectedIndex)
    : nodes;
  const positions = new Map(
    nodes.map((node, index) => {
      if (nodes.length === 1)
        return [node.id, { x: width / 2, y: height / 2 }] as const;
      if (radar && index === selectedIndex) {
        return [node.id, { x: width / 2, y: height / 2 }] as const;
      }
      const orbitIndex = orbitNodes.findIndex((item) => item.id === node.id);
      const angle = (Math.PI * 2 * orbitIndex) / orbitNodes.length - Math.PI / 2;
      return [
        node.id,
        {
          x: width / 2 + Math.cos(angle) * radius,
          y: height / 2 + Math.sin(angle) * radius,
        },
      ] as const;
    }),
  );

  return (
    <div
      data-monkeys-component="workbench-relationship-graph"
      data-workbench-relationship-variant={variant}
      className={cn(
        "relative min-w-0 overflow-hidden rounded-lg border",
        radar
          ? "min-h-[42rem]"
          : "overflow-x-auto border-border/70 bg-card",
        className,
      )}
      style={radar ? {
        backgroundColor: "hsl(var(--radar-canvas, var(--background)))",
        borderColor: "hsl(var(--radar-shell-border, var(--border)))",
        backgroundImage:
          "linear-gradient(hsl(var(--radar-grid, var(--border)) / 0.28) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--radar-grid, var(--border)) / 0.28) 1px, transparent 1px), radial-gradient(circle at center, hsl(var(--primary) / 0.1), transparent 45%)",
        backgroundSize: "4rem 4rem, 4rem 4rem, 100% 100%",
      } : undefined}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className={cn(radar ? "absolute inset-0 size-full" : "min-w-[42rem]")}
        preserveAspectRatio={radar ? "xMidYMid meet" : undefined}
        role="img"
        aria-label={ariaLabel}
      >
        {edges.map((edge, index) => {
          const source = positions.get(edge.sourceId);
          const target = positions.get(edge.targetId);
          if (!source || !target) return null;
          return (
            <line
              key={edge.id || `${edge.sourceId}:${edge.targetId}:${index}`}
              x1={source.x}
              y1={source.y}
              x2={target.x}
              y2={target.y}
              className={radar ? undefined : "stroke-border"}
              strokeWidth={radar ? "2" : "1.5"}
              style={radar ? {
                stroke: "hsl(var(--radar-axis, var(--primary)) / 0.46)",
                filter: "drop-shadow(0 0 0.35rem hsl(var(--primary) / 0.34))",
              } : undefined}
            >
              <title>
                {edge.label || `${edge.sourceId} to ${edge.targetId}`}
              </title>
            </line>
          );
        })}
        {nodes.map((node) => {
          const point = positions.get(node.id)!;
          const selected = node.id === selectedNodeId;
          const tone = node.tone || "muted";
          const radarNodeStyle: CSSProperties | undefined = radar ? {
            color: radarToneColor(tone),
          } : undefined;
          return (
            <g
              key={node.id}
              transform={`translate(${point.x}, ${point.y})`}
              role={onNodeSelect ? "button" : undefined}
              tabIndex={onNodeSelect ? 0 : undefined}
              aria-label={
                node.meta ? `${node.label}, ${node.meta}` : node.label
              }
              aria-pressed={onNodeSelect ? selected : undefined}
              onClick={onNodeSelect ? () => onNodeSelect(node) : undefined}
              onKeyDown={(event) => activateNode(event, node, onNodeSelect)}
              className={cn(
                radar ? "outline-none" : toneClassNames[tone],
                onNodeSelect && "cursor-pointer outline-none",
              )}
              style={radarNodeStyle}
            >
              <circle
                r={radar && selected ? "50" : radar ? "40" : "36"}
                className={cn(
                  "stroke-1 transition-[stroke-width] duration-150 motion-reduce:transition-none",
                  selected && "stroke-[3]",
                )}
                style={radar ? {
                  fill: radarToneColor(tone, selected ? 0.22 : 0.12),
                  stroke: radarToneColor(tone, selected ? 1 : 0.78),
                  filter: `drop-shadow(0 0 ${selected ? "1.4rem" : "0.75rem"} ${radarToneColor(tone, 0.62)})`,
                } : undefined}
              />
              <text
                textAnchor="middle"
                y={node.meta ? -3 : 4}
                className={cn(
                  "pointer-events-none fill-current font-semibold",
                  radar ? "text-[0.875rem]" : "text-xs",
                )}
              >
                {node.label.slice(0, 18)}
              </text>
              {node.meta ? (
                <text
                  textAnchor="middle"
                  y="16"
                  className={cn(
                    "pointer-events-none text-[0.625rem]",
                    radar ? "fill-current opacity-70" : "fill-muted-foreground",
                  )}
                >
                  {node.meta.slice(0, 22)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
